import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import SouthRoundedIcon from '@mui/icons-material/SouthRounded';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { compact, filter, find, includes, join, last, lowerCase, map, orderBy, size } from 'es-toolkit/compat';
import { AgentStreamAction, AgentStreamProgress, TokenTrace } from 'interfaces/agentSession';
import React from 'react';

import { getTranslucentSurfaceStyles } from 'components/theme/theme';
import { ChatMessage, CopilotConversation } from './CopilotConversation';
import { SessionTitleHeader } from './CopilotConversation/SessionTitleHeader';
import { normalizeOptionText } from './helpers';

const gentleBreathe = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.7; }
  25% { transform: scale(1.05) rotate(-3deg); opacity: 0.85; }
  50% { transform: scale(1.1) rotate(0deg); opacity: 1; }
  75% { transform: scale(1.05) rotate(3deg); opacity: 0.85; }
`;

type LabOption = {
  value: string;
  text: string;
};

type StudyOption = {
  value: string;
  text: string;
};

type ScopeMismatchState = {
  currentLabId?: string | null;
  currentStudyIds: string[];
  sessionLabId?: string | null;
  sessionStudyIds: string[];
};

const HEADER_COLLAPSE_SCROLL_THRESHOLD = 24;
const HEADER_REOPEN_SCROLL_DELTA = 12;
const HEADER_TRANSITION_SETTLE_MS = 300;
const CHAT_STICKY_BOTTOM_THRESHOLD_PX = 96;
const chatScrollTopBySessionId = new Map<string, number>();

type CopilotChatTabProps = {
  activeSessionId?: string | null;
  canViewMultipleLabs: boolean;
  canViewTokenUsageDebug?: boolean;
  labOptions: LabOption[];
  labId: string | null;
  controlsDisabled?: boolean;
  onLabChange: (newLabId: string) => void;
  studyOptions: Array<{ value: string; text: string }>;
  selectedStudyIds: string[];
  onStudyIdsChange: (studyIds: string[]) => void;
  onStartNewChat: () => void;
  isChatLoading: boolean;
  messages: ChatMessage[];
  isStreaming: boolean;
  isWorkspaceOpen: boolean;
  onRetry: (userMessage: string) => void;
  agentStatus: string | null;
  agentProgress: AgentStreamProgress | null;
  agentActions: AgentStreamAction[];
  sessionTitle?: string;
  suggestedSessionTitle?: string | null;
  onResumeSuggestedSession?: () => void;
  tokenTraces?: TokenTrace[];
  scopeMismatch?: ScopeMismatchState | null;
  sessionStudyNames?: Record<string, string> | null;
  onStartNewChatForCurrentContext?: () => void;
  onSwitchToSessionContext?: () => void;
  onUsePromptSuggestion: (prompt: string) => void;
  onSendPromptSuggestion: (prompt: string) => void;
  promptSuggestionsDisabled?: boolean;
};

// Presentational chat tab: filters, new chat action, and conversation/empty state.
export const CopilotChatTab: React.FC<CopilotChatTabProps> = ({
  activeSessionId,
  canViewMultipleLabs,
  canViewTokenUsageDebug = false,
  labOptions,
  labId,
  controlsDisabled = false,
  onLabChange,
  studyOptions,
  selectedStudyIds,
  onStudyIdsChange,
  onStartNewChat,
  isChatLoading,
  messages,
  isStreaming,
  isWorkspaceOpen,
  onRetry,
  agentStatus,
  agentProgress,
  agentActions,
  sessionTitle,
  suggestedSessionTitle,
  onResumeSuggestedSession,
  tokenTraces,
  scopeMismatch,
  onStartNewChatForCurrentContext,
  onSwitchToSessionContext,
  onUsePromptSuggestion,
  onSendPromptSuggestion,
  promptSuggestionsDisabled = false,
}) => {
  const stickyHeaderRef = React.useRef<HTMLDivElement | null>(null);
  const conversationBottomAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const isHeaderCollapsedRef = React.useRef(false);
  const collapseAnchorRef = React.useRef(0);
  const ignoreScrollUntilRef = React.useRef(0);
  const lastScrollTopRef = React.useRef(0);
  const shouldStickConversationToBottomRef = React.useRef(true);
  const previousConversationSnapshotRef = React.useRef<{
    sessionId: string | null;
    messageCount: number;
    lastMessageId: string | null;
    lastMessageContent: string;
    lastMessageIsStreaming: boolean;
    agentStatus: string | null;
    agentProgressText: string | null;
    agentProgressValue: number | null;
    actionSignature: string;
    isChatLoading: boolean;
  } | null>(null);
  const [hasScrolledPastCollapseThreshold, setHasScrolledPastCollapseThreshold] = React.useState(false);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = React.useState(false);
  const [showScrollToBottomButton, setShowScrollToBottomButton] = React.useState(false);

  // Normalize/sort labels here so parent can pass raw study options.
  const sessionTitleText = sessionTitle?.trim() || '';
  const showSessionTitle = Boolean(sessionTitleText && size(messages) > 0);
  const sortedStudyOptions = React.useMemo(
    () =>
      orderBy(
        map(studyOptions, (option) => ({ ...option, text: normalizeOptionText(option.text) })),
        [(option) => lowerCase(normalizeOptionText(option.text))]
      ),
    [studyOptions]
  );
  const selectedLabText = React.useMemo(
    () => (canViewMultipleLabs ? find(labOptions, (labOption) => labOption.value === labId)?.text || null : null),
    [canViewMultipleLabs, labId, labOptions]
  );
  const selectedStudyTexts = React.useMemo(
    () =>
      map(
        filter(sortedStudyOptions, (option) => includes(selectedStudyIds, option.value)),
        (option) => normalizeOptionText(option.text)
      ),
    [selectedStudyIds, sortedStudyOptions]
  );
  const resolveScopeSummary = React.useCallback(
    ({ labId: scopeLabId, studyIds }: { labId?: string | null; studyIds: string[] }) => {
      const resolvedLabText = scopeLabId
        ? find(labOptions, (labOption) => labOption.value === scopeLabId)?.text || scopeLabId
        : null;
      const resolvedStudyTexts = map(studyIds, (studyId) => {
        const matchedStudy = find(sortedStudyOptions, (option) => option.value === studyId);
        return normalizeOptionText(matchedStudy?.text || studyId);
      });
      const studyHeadline =
        size(resolvedStudyTexts) === 0
          ? null
          : size(resolvedStudyTexts) === 1
          ? resolvedStudyTexts[0]
          : `${resolvedStudyTexts[0]} +${size(resolvedStudyTexts) - 1}`;

      return join(
        compact([
          resolvedLabText ? `Lab: ${resolvedLabText}` : null,
          studyHeadline ? `Studies: ${studyHeadline}` : null,
        ]),
        ' · '
      );
    },
    [labOptions, sortedStudyOptions]
  );
  const currentScopeSummary = React.useMemo(
    () =>
      scopeMismatch
        ? resolveScopeSummary({ labId: scopeMismatch.currentLabId, studyIds: scopeMismatch.currentStudyIds })
        : null,
    [resolveScopeSummary, scopeMismatch]
  );
  const sessionScopeSummary = React.useMemo(
    () =>
      scopeMismatch
        ? resolveScopeSummary({ labId: scopeMismatch.sessionLabId, studyIds: scopeMismatch.sessionStudyIds })
        : null,
    [resolveScopeSummary, scopeMismatch]
  );
  const studyScopeHeadline = React.useMemo(() => {
    if (size(selectedStudyTexts) === 0) {
      return 'All studies';
    }

    if (size(selectedStudyTexts) === 1) {
      return selectedStudyTexts[0];
    }

    return `${selectedStudyTexts[0]} +${size(selectedStudyTexts) - 1}`;
  }, [selectedStudyTexts]);
  const collapsedScopeHeadline = React.useMemo(
    () => join(compact([selectedLabText ? `Lab: ${selectedLabText}` : null, `Studies: ${studyScopeHeadline}`]), ' · '),
    [selectedLabText, studyScopeHeadline]
  );
  const isHeaderExpanded = !hasScrolledPastCollapseThreshold || !isHeaderCollapsed;
  const showHeaderToggle = hasScrolledPastCollapseThreshold && size(messages) > 0;
  const showNewChatButton = size(messages) > 0 || isChatLoading || isStreaming;
  const getScrollContainer = React.useCallback(() => stickyHeaderRef.current?.parentElement || null, []);
  const persistScrollTop = React.useCallback(
    (scrollTop: number) => {
      if (!activeSessionId) {
        return;
      }

      chatScrollTopBySessionId.set(activeSessionId, Math.max(0, scrollTop));
    },
    [activeSessionId]
  );
  const updateShouldStickToBottom = React.useCallback(
    (scrollContainer: HTMLElement) => {
      const distanceFromBottom =
        scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
      const shouldStickToBottom = distanceFromBottom <= CHAT_STICKY_BOTTOM_THRESHOLD_PX;

      shouldStickConversationToBottomRef.current = shouldStickToBottom;
      setShowScrollToBottomButton(size(messages) > 0 && !shouldStickToBottom);
    },
    [messages]
  );
  const conversationSnapshot = React.useMemo(() => {
    const lastMessage = last(messages);

    return {
      sessionId: activeSessionId || null,
      messageCount: size(messages),
      lastMessageId: lastMessage?.id || null,
      lastMessageContent: lastMessage?.content || '',
      lastMessageIsStreaming: Boolean(lastMessage?.isStreaming),
      agentStatus: agentStatus || null,
      agentProgressText: agentProgress?.text || null,
      agentProgressValue: agentProgress?.progress ?? null,
      actionSignature: join(
        map(agentActions, (action) => `${action.id}:${action.status}:${action.label || ''}`),
        '|'
      ),
      isChatLoading,
    };
  }, [
    activeSessionId,
    agentActions,
    agentProgress?.progress,
    agentProgress?.text,
    agentStatus,
    isChatLoading,
    messages,
  ]);

  React.useLayoutEffect(() => {
    const scrollContainer = getScrollContainer();
    if (!scrollContainer) {
      return;
    }

    const savedScrollTop = activeSessionId ? chatScrollTopBySessionId.get(activeSessionId) : undefined;

    if (typeof savedScrollTop === 'number') {
      scrollContainer.scrollTop = savedScrollTop;
      lastScrollTopRef.current = savedScrollTop;
      updateShouldStickToBottom(scrollContainer);
      return;
    }

    if (size(messages) > 0) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }

    lastScrollTopRef.current = scrollContainer.scrollTop;
    updateShouldStickToBottom(scrollContainer);
  }, [activeSessionId, getScrollContainer, messages, updateShouldStickToBottom]);

  React.useEffect(() => {
    const scrollContainer = getScrollContainer();
    if (!scrollContainer) {
      return undefined;
    }

    const collapseHeader = () => {
      if (isHeaderCollapsedRef.current) {
        return;
      }

      isHeaderCollapsedRef.current = true;
      collapseAnchorRef.current = lastScrollTopRef.current;
      ignoreScrollUntilRef.current = Date.now() + HEADER_TRANSITION_SETTLE_MS;
      setIsHeaderCollapsed(true);
    };

    const expandHeader = () => {
      if (!isHeaderCollapsedRef.current) {
        return;
      }

      isHeaderCollapsedRef.current = false;
      collapseAnchorRef.current = 0;
      ignoreScrollUntilRef.current = Date.now() + HEADER_TRANSITION_SETTLE_MS;
      setIsHeaderCollapsed(false);
    };

    const handleScroll = () => {
      const nextScrollTop = scrollContainer.scrollTop;
      const previousScrollTop = lastScrollTopRef.current;
      lastScrollTopRef.current = nextScrollTop;
      persistScrollTop(nextScrollTop);
      updateShouldStickToBottom(scrollContainer);

      // During MUI Collapse transitions, skip state logic to prevent feedback loops.
      if (Date.now() < ignoreScrollUntilRef.current) {
        return;
      }

      const isPastThreshold = nextScrollTop > HEADER_COLLAPSE_SCROLL_THRESHOLD;
      setHasScrolledPastCollapseThreshold(isPastThreshold);

      if (!isPastThreshold) {
        if (isHeaderCollapsedRef.current) {
          isHeaderCollapsedRef.current = false;
          collapseAnchorRef.current = 0;
          setIsHeaderCollapsed(false);
        }

        return;
      }

      const isScrollingDown = nextScrollTop > previousScrollTop;
      const isScrollingUp = nextScrollTop < previousScrollTop;

      if (isScrollingDown) {
        collapseAnchorRef.current = nextScrollTop;
        collapseHeader();
        return;
      }

      if (isScrollingUp && isHeaderCollapsedRef.current) {
        const hasScrolledEnough = nextScrollTop <= collapseAnchorRef.current - HEADER_REOPEN_SCROLL_DELTA;

        if (hasScrolledEnough) {
          expandHeader();
        }
      }
    };

    lastScrollTopRef.current = scrollContainer.scrollTop;
    persistScrollTop(lastScrollTopRef.current);
    updateShouldStickToBottom(scrollContainer);
    handleScroll();
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      persistScrollTop(lastScrollTopRef.current);
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [activeSessionId, getScrollContainer, persistScrollTop, updateShouldStickToBottom]);

  React.useEffect(() => {
    const scrollContainer = getScrollContainer();
    const bottomAnchor = conversationBottomAnchorRef.current;
    const previousSnapshot = previousConversationSnapshotRef.current;

    previousConversationSnapshotRef.current = conversationSnapshot;

    if (!scrollContainer || !bottomAnchor || !previousSnapshot) {
      return;
    }

    if (previousSnapshot.sessionId !== conversationSnapshot.sessionId) {
      return;
    }

    const didAppendMessage =
      conversationSnapshot.messageCount > previousSnapshot.messageCount ||
      conversationSnapshot.lastMessageId !== previousSnapshot.lastMessageId ||
      conversationSnapshot.lastMessageContent !== previousSnapshot.lastMessageContent;
    const didStreamingStateAdvance =
      conversationSnapshot.lastMessageIsStreaming &&
      (conversationSnapshot.agentStatus !== previousSnapshot.agentStatus ||
        conversationSnapshot.agentProgressText !== previousSnapshot.agentProgressText ||
        conversationSnapshot.agentProgressValue !== previousSnapshot.agentProgressValue ||
        conversationSnapshot.actionSignature !== previousSnapshot.actionSignature ||
        conversationSnapshot.isChatLoading !== previousSnapshot.isChatLoading);

    if (!(didAppendMessage || didStreamingStateAdvance) || !shouldStickConversationToBottomRef.current) {
      return;
    }

    const nextScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
    scrollContainer.scrollTop = nextScrollTop;
    lastScrollTopRef.current = nextScrollTop;
    persistScrollTop(nextScrollTop);
    setShowScrollToBottomButton(false);
    bottomAnchor.scrollIntoView({ behavior: didStreamingStateAdvance ? 'smooth' : 'auto', block: 'end' });
  }, [conversationSnapshot, getScrollContainer, persistScrollTop]);

  const handleScrollToBottom = React.useCallback(() => {
    const scrollContainer = getScrollContainer();
    const bottomAnchor = conversationBottomAnchorRef.current;

    if (!scrollContainer || !bottomAnchor) {
      return;
    }

    const nextScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
    shouldStickConversationToBottomRef.current = true;
    scrollContainer.scrollTop = nextScrollTop;
    lastScrollTopRef.current = nextScrollTop;
    persistScrollTop(nextScrollTop);
    setShowScrollToBottomButton(false);
    bottomAnchor.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [getScrollContainer, persistScrollTop]);

  const handleToggleHeader = () => {
    const nextValue = !isHeaderCollapsedRef.current;
    isHeaderCollapsedRef.current = nextValue;
    collapseAnchorRef.current = nextValue ? lastScrollTopRef.current : 0;
    ignoreScrollUntilRef.current = Date.now() + HEADER_TRANSITION_SETTLE_MS;
    setIsHeaderCollapsed(nextValue);
  };

  return (
    <>
      <Box
        ref={stickyHeaderRef}
        data-testid="copilot-chat-sticky-header"
        sx={(theme) => ({
          ...getTranslucentSurfaceStyles(theme),
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          pb: 1,
          px: 2,
          pt: 0,
          mx: -2,
          mt: 0,
          mb: 0.5,
          boxSizing: 'border-box',
          borderRadius: 0,
          boxShadow:
            theme.palette.mode === 'light' ? '0 12px 28px rgba(15, 23, 42, 0.08)' : '0 14px 30px rgba(0, 0, 0, 0.24)',
        })}
      >
        <Collapse in={isHeaderExpanded} timeout="auto" unmountOnExit>
          <Stack spacing={1.5}>
            {canViewMultipleLabs && size(labOptions) > 0 && (
              <Select
                size="small"
                value={labId || ''}
                onChange={(event) => onLabChange(event.target.value as string)}
                displayEmpty
                fullWidth
                disabled={controlsDisabled}
              >
                {map(labOptions, (lab) => (
                  <MenuItem key={lab.value} value={lab.value}>
                    {lab.text}
                  </MenuItem>
                ))}
              </Select>
            )}
            {studyOptions && (
              <Autocomplete
                multiple
                size="small"
                disabled={controlsDisabled}
                options={sortedStudyOptions}
                value={filter(sortedStudyOptions, (option) => includes(selectedStudyIds, option.value)) || []}
                getOptionLabel={(option: StudyOption) => normalizeOptionText(option.text)}
                isOptionEqualToValue={(option: { value: string }, value: { value: string }) =>
                  option?.value === value?.value
                }
                onChange={(_event: unknown, selected: Array<{ value: string }>) => {
                  const ids = compact(map(selected, (opt) => opt?.value));
                  onStudyIdsChange(ids);
                }}
                renderInput={(params) => <TextField {...params} label="Studies" />}
              />
            )}
          </Stack>
        </Collapse>
        <Collapse in={!isHeaderExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ py: 0.5 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.2, letterSpacing: '0.08em' }}
            >
              Current scope
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {collapsedScopeHeadline}
            </Typography>
          </Box>
        </Collapse>
        <Stack
          data-testid="copilot-chat-header-actions"
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ minWidth: 0 }}
        >
          <Stack direction="column" spacing={0} sx={{ minWidth: 0, flex: 1 }}>
            {showSessionTitle && <SessionTitleHeader title={sessionTitleText} />}
            {!showSessionTitle && suggestedSessionTitle && onResumeSuggestedSession && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, pl: 0.5 }}>
                  {size(selectedStudyIds) > 0 ? 'Last session on this study' : 'Last session on this lab'}
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<HistoryRoundedIcon fontSize="small" />}
                  onClick={onResumeSuggestedSession}
                  disabled={controlsDisabled}
                  sx={{
                    flexShrink: 1,
                    minWidth: 0,
                    textTransform: 'none',
                    fontWeight: 500,
                    justifyContent: 'flex-start',
                    '& .MuiButton-startIcon': { mr: 0.5 },
                  }}
                >
                  <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                    {suggestedSessionTitle}
                  </Typography>
                </Button>
              </>
            )}
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            {showScrollToBottomButton && (
              <Button
                size="small"
                variant="text"
                startIcon={<SouthRoundedIcon fontSize="small" />}
                aria-label="Scroll to bottom"
                onClick={handleScrollToBottom}
                sx={{ flexShrink: 0, minWidth: 0, px: 1 }}
              >
                Latest
              </Button>
            )}
            {showHeaderToggle && (
              <IconButton
                size="small"
                aria-label={isHeaderExpanded ? 'Collapse chat header' : 'Expand chat header'}
                onClick={handleToggleHeader}
              >
                {isHeaderExpanded ? (
                  <ExpandLessRoundedIcon fontSize="small" />
                ) : (
                  <ExpandMoreRoundedIcon fontSize="small" />
                )}
              </IconButton>
            )}
            {showNewChatButton && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddRoundedIcon fontSize="small" />}
                onClick={onStartNewChat}
                disabled={isChatLoading || controlsDisabled}
                sx={{ flexShrink: 0 }}
              >
                New chat
              </Button>
            )}
          </Stack>
        </Stack>
        {scopeMismatch && sessionScopeSummary && currentScopeSummary && onStartNewChatForCurrentContext && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{
              alignItems: 'flex-start',
              '& .MuiAlert-message': {
                width: '100%',
              },
            }}
          >
            <Stack spacing={1}>
              <Typography variant="body2">
                This Copilot session is still scoped to {sessionScopeSummary}, while the current page is on{' '}
                {currentScopeSummary}.
              </Typography>
              <Typography variant="body2">
                You can review the saved report here, but you cannot run Copilot operations until you switch to the
                session context or open a new chat for the current page.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  color="warning"
                  variant="contained"
                  onClick={onSwitchToSessionContext}
                  disabled={controlsDisabled || isChatLoading || !onSwitchToSessionContext}
                >
                  Switch to session context
                </Button>
                <Button
                  size="small"
                  color="warning"
                  variant="contained"
                  onClick={onStartNewChatForCurrentContext}
                  disabled={controlsDisabled || isChatLoading}
                >
                  Open new chat for current context
                </Button>
              </Stack>
            </Stack>
          </Alert>
        )}
      </Box>

      {size(messages) === 0 && !isChatLoading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            gap: 1.5,
            py: 6,
          }}
        >
          <FaceRetouchingNaturalIcon
            sx={{
              fontSize: 48,
              color: 'secondary.main',
              animation: `${gentleBreathe} 4s ease-in-out infinite`,
            }}
          />
          {!controlsDisabled && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                animation: 'copilotEmptyFadeIn 0.5s ease-out',
                '@keyframes copilotEmptyFadeIn': {
                  from: { opacity: 0, transform: 'translateY(8px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Ready when you are
              </Typography>
              <Typography variant="caption" color="text.disabled" textAlign="center" sx={{ maxWidth: 240 }}>
                {isWorkspaceOpen
                  ? 'Send a message to start exploring.'
                  : 'Send a message or open the report canvas to get started.'}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <CopilotConversation
          messages={messages}
          isLoading={isStreaming || isChatLoading}
          onRetry={onRetry}
          agentStatus={agentStatus}
          agentProgress={agentProgress}
          agentActions={agentActions}
          canViewTokenUsageDebug={canViewTokenUsageDebug}
          tokenTraces={tokenTraces}
          bottomAnchorRef={conversationBottomAnchorRef}
          onUsePromptSuggestion={onUsePromptSuggestion}
          onSendPromptSuggestion={onSendPromptSuggestion}
          promptSuggestionsDisabled={promptSuggestionsDisabled}
          studyId={selectedStudyIds[0]}
        />
      )}
    </>
  );
};
